import React, { useState, useEffect } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import {
  HardwareBag,
  HardwareBagStatus,
  FastenerSpec,
  FastenerType,
  FastenerGrade,
  FastenerFinish,
} from '../../types/teardown';
import { MechanicalButton } from '../common/MechanicalButton';
import {
  Wrench,
  X,
  Plus,
  Trash2,
  Tag,
  ShieldCheck,
  Check,
} from 'lucide-react';

export interface HardwareBaggieModalProps {
  isOpen: boolean;
  onClose: () => void;
  bag?: HardwareBag | null;
  initialSubAssemblyId?: string;
  onSave?: (savedBag: HardwareBag) => void;
}

const FASTENER_TYPES: FastenerType[] = [
  'Hex Head Bolt',
  'Carriage Bolt',
  'Socket Head Cap Screw',
  'Phillips Truss Head Screw',
  'Hex Nut',
  'Castle Nut',
  'Stover Lock Nut',
  'Flat Washer',
  'Wave Washer',
  'Split Lock Washer',
  'Cotter Pin',
  'J-Nut / Speed Clip',
  'Specialty Stud',
];

const FASTENER_GRADES: FastenerGrade[] = [
  'Grade 2',
  'Grade 5',
  'Grade 8',
  'Stainless 304',
  'Stainless 316',
  'Metric 8.8',
  'Metric 10.9',
  'OEM Induction Hardened',
];

const FASTENER_FINISHES: FastenerFinish[] = [
  'Black Oxide',
  'Yellow Cadmium',
  'Clear Zinc',
  'Phosphate & Oil',
  'Chrome',
  'Raw / As-Found',
  'Painted',
];

const BAG_STATUS_OPTIONS: { id: HardwareBagStatus; label: string; badge: string }[] = [
  { id: 'tagged', label: 'Tagged As-Found', badge: 'bg-shop-well text-shop-muted border-shop-border' },
  { id: 'cleaning_prep', label: 'Cleaning & Degreasing', badge: 'bg-safety-amber/20 text-safety-amber border-safety-amber/40' },
  { id: 'plated_restored', label: 'Plated / Restored', badge: 'bg-safety-blue/20 text-safety-blue border-safety-blue/40' },
  { id: 'replaced', label: 'New Fasteners Acquired', badge: 'bg-safety-cyan/20 text-safety-cyan border-safety-cyan/40' },
  { id: 'reinstalled', label: 'Torqued & Reinstalled', badge: 'bg-safety-green/20 text-safety-green border-safety-green/40' },
];

export const HardwareBaggieModal: React.FC<HardwareBaggieModalProps> = ({
  isOpen,
  onClose,
  bag,
  initialSubAssemblyId,
  onSave,
}) => {
  const { state, dispatch } = useRestoMod();

  // Selected subassembly
  const [subAssemblyId, setSubAssemblyId] = useState<string>(
    bag?.subAssemblyId || initialSubAssemblyId || state.subAssemblies[0]?.id || ''
  );

  // Baggie metadata
  const [bagLabel, setBagLabel] = useState<string>('');
  const [binLocation, setBinLocation] = useState<string>('');
  const [status, setStatus] = useState<HardwareBagStatus>('tagged');
  const [torqueSpec, setTorqueSpec] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [reusable, setReusable] = useState<boolean>(true);
  const [replacementsOrdered, setReplacementsOrdered] = useState<boolean>(false);

  // Fasteners list
  const [fasteners, setFasteners] = useState<FastenerSpec[]>([]);

  // Fastener editor sub-form state
  const [newType, setNewType] = useState<string>('Hex Head Bolt');
  const [newSize, setNewSize] = useState<string>('3/8"-16 x 1-1/4"');
  const [newGrade, setNewGrade] = useState<string>('Grade 5');
  const [newFinish, setNewFinish] = useState<string>('Yellow Cadmium');
  const [newQtyOrig, setNewQtyOrig] = useState<number>(4);
  const [newQtyUsable, setNewQtyUsable] = useState<number>(4);
  const [newReplacementReq, setNewReplacementReq] = useState<boolean>(false);
  const [newHeadmark, setNewHeadmark] = useState<string>('');

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  // Reset or populate fields when modal opens or bag prop changes
  useEffect(() => {
    if (bag) {
      setSubAssemblyId(bag.subAssemblyId);
      setBagLabel(bag.bagLabel);
      setBinLocation(bag.binLocation);
      setStatus(bag.status);
      setTorqueSpec(bag.torqueSpec || '');
      setNotes(bag.notes);
      setReusable(bag.reusable);
      setReplacementsOrdered(bag.replacementsOrdered);
      setFasteners(bag.fasteners);
    } else {
      const defaultSubId = initialSubAssemblyId || state.subAssemblies[0]?.id || '';
      const matchingSub = state.subAssemblies.find((s) => s.id === defaultSubId);
      const nextBagNum = state.hardwareBags.length + 1;
      const formattedNum = String(nextBagNum).padStart(3, '0');

      setSubAssemblyId(defaultSubId);
      setBagLabel(`BAG-${formattedNum}: ${matchingSub ? matchingSub.name : 'Fastener Group'}`);
      setBinLocation(matchingSub ? matchingSub.binNumber : 'BIN-A01');
      setStatus('tagged');
      setTorqueSpec('');
      setNotes('');
      setReusable(true);
      setReplacementsOrdered(false);
      setFasteners([
        {
          id: `fast_${Date.now()}_1`,
          type: 'Hex Head Bolt',
          size: '3/8"-16 x 1-1/4"',
          grade: 'Grade 5',
          finish: 'Yellow Cadmium',
          quantityOriginal: 4,
          quantityUsable: 4,
          replacementRequired: false,
          headmarkNotes: 'Original factory radial marks',
        },
      ]);
    }
    setShowDeleteConfirm(false);
  }, [bag, initialSubAssemblyId, state.subAssemblies, state.hardwareBags.length, isOpen]);

  // When subassembly changes, automatically sync binLocation if empty or unchanged
  const handleSubAssemblyChange = (newSubId: string) => {
    setSubAssemblyId(newSubId);
    const sub = state.subAssemblies.find((s) => s.id === newSubId);
    if (sub && (!binLocation || binLocation.startsWith('BIN-'))) {
      setBinLocation(sub.binNumber);
    }
  };

  // Add a fastener to the list
  const handleAddFastener = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSize.trim()) return;

    const newFastener: FastenerSpec = {
      id: `fast_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      type: newType,
      size: newSize.trim(),
      grade: newGrade,
      finish: newFinish,
      quantityOriginal: Math.max(1, newQtyOrig),
      quantityUsable: Math.max(0, newQtyUsable),
      replacementRequired: newReplacementReq,
      headmarkNotes: newHeadmark.trim() || undefined,
    };

    setFasteners([...fasteners, newFastener]);
    // Reset quick fields
    setNewHeadmark('');
  };

  // Toggle replacement requirement on existing fastener
  const handleToggleReplacement = (fId: string) => {
    setFasteners(
      fasteners.map((f) =>
        f.id === fId ? { ...f, replacementRequired: !f.replacementRequired } : f
      )
    );
  };

  // Remove fastener from list
  const handleRemoveFastener = (fId: string) => {
    setFasteners(fasteners.filter((f) => f.id !== fId));
  };

  // Update fastener usable quantity
  const handleUpdateUsableQty = (fId: string, usable: number) => {
    setFasteners(
      fasteners.map((f) => {
        if (f.id === fId) {
          const clamped = Math.max(0, Math.min(f.quantityOriginal, usable));
          const needsReplace = clamped < f.quantityOriginal;
          return { ...f, quantityUsable: clamped, replacementRequired: needsReplace };
        }
        return f;
      })
    );
  };

  // Save the Hardware Baggie
  const handleSaveBag = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bagLabel.trim() || !subAssemblyId) return;

    const now = new Date().toISOString();
    const bagPayload: HardwareBag = {
      id: bag?.id || `bag_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      subAssemblyId,
      projectId: state.project.id,
      bagLabel: bagLabel.trim(),
      binLocation: binLocation.trim() || 'Unassigned',
      fasteners,
      torqueSpec: torqueSpec.trim() || undefined,
      notes: notes.trim(),
      reusable,
      replacementsOrdered,
      status,
      createdAt: bag?.createdAt || now,
      updatedAt: now,
    };

    if (bag) {
      dispatch({ type: 'UPDATE_HARDWARE_BAG', payload: bagPayload });
    } else {
      dispatch({ type: 'ADD_HARDWARE_BAG', payload: bagPayload });
    }

    if (onSave) {
      onSave(bagPayload);
    }
    onClose();
  };

  // Delete the Baggie
  const handleDeleteBag = () => {
    if (!bag) return;
    dispatch({ type: 'DELETE_HARDWARE_BAG', payload: bag.id });
    onClose();
  };

  if (!isOpen) return null;

  // Calculation summaries
  const totalFastenersCount = fasteners.reduce((acc, f) => acc + f.quantityOriginal, 0);
  const totalUsableCount = fasteners.reduce((acc, f) => acc + f.quantityUsable, 0);
  const totalReplacementsCount = fasteners.reduce(
    (acc, f) => acc + (f.replacementRequired ? f.quantityOriginal - f.quantityUsable || f.quantityOriginal : 0),
    0
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-shop-dark border-2 border-safety-amber rounded max-w-4xl w-full p-4 sm:p-6 space-y-4 shadow-2xl relative my-8 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-shop-border pb-3 shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="p-1.5 bg-shop-darkest rounded border border-shop-border text-safety-amber">
              <Wrench className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-sm sm:text-base font-bold uppercase tracking-wider text-shop-highlight">
                {bag ? 'Inspect Hardware Baggie Fastener Specs' : 'Create New Hardware Baggie'}
              </h2>
              <p className="text-xs text-shop-muted">
                Fastener grades, thread pitches, surface plating, and torque specifications
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-highlight transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1 pr-1 space-y-5 text-xs font-mono">
          {/* 1. Baggie Metadata Form */}
          <div className="bg-shop-darkest border border-shop-border rounded p-4 space-y-3">
            <h3 className="text-xs font-bold text-shop-highlight uppercase tracking-wider flex items-center gap-1.5 border-b border-shop-border pb-2">
              <Tag className="w-3.5 h-3.5 text-safety-amber" />
              Baggie Identification &amp; Storage Bin
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Baggie Label */}
              <div className="sm:col-span-2 space-y-1">
                <label className="text-shop-muted uppercase block">Hardware Baggie Label *</label>
                <input
                  type="text"
                  required
                  value={bagLabel}
                  onChange={(e) => setBagLabel(e.target.value)}
                  placeholder="e.g. BAG-014: Front Caliper Bracket Bolts"
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-panel border border-shop-border rounded text-shop-light font-bold focus:border-safety-amber focus:outline-none"
                />
              </div>

              {/* Physical Bin Location */}
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Bin / Drawer Location *</label>
                <input
                  type="text"
                  required
                  value={binLocation}
                  onChange={(e) => setBinLocation(e.target.value)}
                  placeholder="e.g. BIN-B02"
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-panel border border-shop-border rounded text-safety-amber font-bold focus:border-safety-amber focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Linked Sub-Assembly */}
              <div className="sm:col-span-2 space-y-1">
                <label className="text-shop-muted uppercase block">Linked Sub-Assembly *</label>
                <select
                  value={subAssemblyId}
                  onChange={(e) => handleSubAssemblyChange(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-panel border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                >
                  {state.subAssemblies.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      [{sub.binNumber}] {sub.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Hardware Status *</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as HardwareBagStatus)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-panel border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                >
                  {BAG_STATUS_OPTIONS.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Torque Specifications */}
              <div className="sm:col-span-2 space-y-1">
                <label className="text-shop-muted uppercase block">Torque Specifications</label>
                <input
                  type="text"
                  value={torqueSpec}
                  onChange={(e) => setTorqueSpec(e.target.value)}
                  placeholder="e.g. 75 lb-ft with engine oil / 35 lb-ft with blue Loctite"
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-panel border border-shop-border rounded text-safety-cyan font-bold focus:border-safety-amber focus:outline-none"
                />
              </div>

              {/* Toggles */}
              <div className="space-y-2 flex flex-col justify-center pt-2">
                <label className="flex items-center gap-2 cursor-pointer select-none text-shop-light min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={reusable}
                    onChange={(e) => setReusable(e.target.checked)}
                    className="w-4 h-4 rounded border-shop-border text-safety-amber focus:ring-0 cursor-pointer"
                  />
                  <span>Originals Reusable</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none text-shop-light min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={replacementsOrdered}
                    onChange={(e) => setReplacementsOrdered(e.target.checked)}
                    className="w-4 h-4 rounded border-shop-border text-safety-amber focus:ring-0 cursor-pointer"
                  />
                  <span>Replacements Ordered</span>
                </label>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <label className="text-shop-muted uppercase block">Technician &amp; Reassembly Notes</label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Disassembly sequence notes, thread sealant required, special washers..."
                className="w-full px-3 py-2 bg-shop-panel border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none font-sans"
              />
            </div>
          </div>

          {/* 2. Fastener Specifications Table */}
          <div className="bg-shop-darkest border border-shop-border rounded p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-shop-border pb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-safety-amber" />
                <h3 className="text-xs font-bold text-shop-highlight uppercase tracking-wider">
                  Fastener Specs &amp; Replacements Ledger
                </h3>
              </div>

              {/* Fasteners Quick Tally */}
              <div className="flex items-center gap-2 text-[11px] font-mono">
                <span className="bg-shop-panel px-2 py-0.5 rounded border border-shop-border text-shop-light">
                  Total: <strong>{totalFastenersCount}</strong>
                </span>
                <span className="bg-shop-panel px-2 py-0.5 rounded border border-shop-border text-safety-green">
                  Usable: <strong>{totalUsableCount}</strong>
                </span>
                {totalReplacementsCount > 0 && (
                  <span className="bg-safety-red/20 px-2 py-0.5 rounded border border-safety-red/40 text-safety-red font-bold">
                    Need Replace: <strong>{totalReplacementsCount}</strong>
                  </span>
                )}
              </div>
            </div>

            {/* Fasteners Listing */}
            {fasteners.length === 0 ? (
              <div className="p-6 text-center text-shop-muted italic">
                No fastener specifications logged for this baggie yet. Add your first item below.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs font-mono border-collapse">
                  <thead>
                    <tr className="border-b border-shop-border text-shop-muted uppercase text-[10px]">
                      <th className="py-2 px-2">Type</th>
                      <th className="py-2 px-2">Size &amp; Pitch</th>
                      <th className="py-2 px-2">Grade</th>
                      <th className="py-2 px-2">Finish</th>
                      <th className="py-2 px-2 text-center">Original</th>
                      <th className="py-2 px-2 text-center">Usable</th>
                      <th className="py-2 px-2 text-center">Replacement</th>
                      <th className="py-2 px-2">Headmark</th>
                      <th className="py-2 px-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-shop-border/40">
                    {fasteners.map((f) => (
                      <tr key={f.id} className="hover:bg-shop-panel/50 transition-colors">
                        <td className="py-2.5 px-2 font-bold text-shop-light">{f.type}</td>
                        <td className="py-2.5 px-2 text-safety-amber font-bold">{f.size}</td>
                        <td className="py-2.5 px-2 text-shop-muted">{f.grade}</td>
                        <td className="py-2.5 px-2 text-shop-muted">{f.finish}</td>
                        <td className="py-2.5 px-2 text-center text-shop-light font-bold">
                          {f.quantityOriginal}
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <input
                            type="number"
                            min={0}
                            max={f.quantityOriginal}
                            value={f.quantityUsable}
                            onChange={(e) =>
                              handleUpdateUsableQty(f.id, parseInt(e.target.value) || 0)
                            }
                            className="w-14 min-h-[44px] px-1.5 py-1 bg-shop-dark border border-shop-border rounded text-center text-safety-green font-bold focus:border-safety-amber focus:outline-none"
                          />
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleToggleReplacement(f.id)}
                            className={`min-h-[44px] px-2 py-1 rounded text-[11px] font-bold uppercase border transition-colors cursor-pointer ${
                              f.replacementRequired
                                ? 'bg-safety-red/20 text-safety-red border-safety-red/50 hover:bg-safety-red/30'
                                : 'bg-safety-green/20 text-safety-green border-safety-green/50 hover:bg-safety-green/30'
                            }`}
                          >
                            {f.replacementRequired ? 'REPLACE' : 'OK'}
                          </button>
                        </td>
                        <td className="py-2.5 px-2 text-shop-muted text-[11px]">
                          {f.headmarkNotes || '—'}
                        </td>
                        <td className="py-2.5 px-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleRemoveFastener(f.id)}
                            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-shop-muted hover:text-safety-red transition-colors"
                            title="Remove fastener spec"
                            aria-label="Remove fastener"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Quick Add Fastener Sub-Form */}
            <div className="pt-3 border-t border-shop-border">
              <span className="text-[11px] font-bold text-safety-amber uppercase block mb-2">
                + Add Fastener Line Item
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="space-y-0.5">
                  <label className="text-[10px] text-shop-muted uppercase">Fastener Type</label>
                  <select
                    value={newType}
                    onChange={(e) => setNewType(e.target.value)}
                    className="w-full min-h-[44px] px-2 py-1 bg-shop-panel border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {FASTENER_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-0.5">
                  <label className="text-[10px] text-shop-muted uppercase">Size &amp; Thread Pitch</label>
                  <input
                    type="text"
                    value={newSize}
                    onChange={(e) => setNewSize(e.target.value)}
                    placeholder="e.g. 7/16-20 x 2-1/2"
                    className="w-full min-h-[44px] px-2 py-1 bg-shop-panel border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none font-bold"
                  />
                </div>

                <div className="space-y-0.5">
                  <label className="text-[10px] text-shop-muted uppercase">Grade</label>
                  <select
                    value={newGrade}
                    onChange={(e) => setNewGrade(e.target.value)}
                    className="w-full min-h-[44px] px-2 py-1 bg-shop-panel border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {FASTENER_GRADES.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-0.5">
                  <label className="text-[10px] text-shop-muted uppercase">Plating / Finish</label>
                  <select
                    value={newFinish}
                    onChange={(e) => setNewFinish(e.target.value)}
                    className="w-full min-h-[44px] px-2 py-1 bg-shop-panel border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {FASTENER_FINISHES.map((fin) => (
                      <option key={fin} value={fin}>
                        {fin}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                <div className="space-y-0.5">
                  <label className="text-[10px] text-shop-muted uppercase">Qty Original</label>
                  <input
                    type="number"
                    min={1}
                    value={newQtyOrig}
                    onChange={(e) => {
                      const v = parseInt(e.target.value) || 1;
                      setNewQtyOrig(v);
                      setNewQtyUsable(v);
                    }}
                    className="w-full min-h-[44px] px-2 py-1 bg-shop-panel border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  />
                </div>

                <div className="space-y-0.5">
                  <label className="text-[10px] text-shop-muted uppercase">Qty Usable</label>
                  <input
                    type="number"
                    min={0}
                    max={newQtyOrig}
                    value={newQtyUsable}
                    onChange={(e) => {
                      const v = parseInt(e.target.value) || 0;
                      setNewQtyUsable(v);
                      setNewReplacementReq(v < newQtyOrig);
                    }}
                    className="w-full min-h-[44px] px-2 py-1 bg-shop-panel border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  />
                </div>

                <div className="space-y-0.5 sm:col-span-2">
                  <label className="text-[10px] text-shop-muted uppercase">Headmark Notes</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newHeadmark}
                      onChange={(e) => setNewHeadmark(e.target.value)}
                      placeholder="e.g. TR with 6 radial tick marks"
                      className="w-full min-h-[44px] px-2 py-1 bg-shop-panel border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={handleAddFastener}
                      aria-label="Add fastener item"
                      className="min-h-[44px] px-4 bg-safety-amber hover:bg-[#fbbf24] text-shop-darkest font-bold uppercase rounded flex items-center justify-center shrink-0 cursor-pointer"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Actions Footer */}
        <div className="pt-3 border-t border-shop-border flex items-center justify-between gap-3 shrink-0">
          <div>
            {bag && (
              <>
                {showDeleteConfirm ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-safety-red font-bold">Delete this baggie?</span>
                    <button
                      type="button"
                      onClick={handleDeleteBag}
                      className="min-h-[44px] px-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded uppercase text-xs cursor-pointer transition-colors"
                    >
                      Yes, Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="min-h-[44px] px-3 bg-shop-well text-shop-light rounded uppercase text-xs cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="min-h-[44px] px-3 text-shop-muted hover:text-safety-red text-xs font-mono uppercase flex items-center gap-1.5 cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Baggie</span>
                  </button>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <MechanicalButton variant="outline" size="default" onClick={onClose}>
              Cancel
            </MechanicalButton>
            <MechanicalButton
              variant="primary"
              size="default"
              icon={<Check className="w-4 h-4" />}
              onClick={handleSaveBag}
            >
              Save Hardware Baggie
            </MechanicalButton>
          </div>
        </div>
      </div>
    </div>
  );
};
